import {objectFromEntries, filterMapToNonNulls} from '@benchling/js-utils';
import _ from 'underscore';

import {declarativeWriteRESTObjsToApollo} from 'graphql-client/ApolloWrappers';
import {GraphQLTypes} from 'graphql-client/declarative-translations/external/GraphQLTypes';
import {RESTTypes} from 'graphql-client/declarative-translations/external/RESTTypes';
import {InaccessibleInfo as InaccessibleInfoREST} from 'graphql-client/declarative-translations/external/RESTTypes';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import Store from 'lib/Store';

import Syncer from './Syncer';
import {
  AccessibleOrInaccessibleSyncerConfig,
  BaseModelSyncerConfig,
  DeclarativeSyncerConfig,
  MaybeCanGetIDFromGraphQL,
  RootCollectionSyncerConfig,
  SymmetricModelSyncerConfig,
  SyncDeclarationInterface,
  SyncInterface,
  TDeclarationName,
  TDeclarativeFragmentName,
  TFragmentSyncer,
  TFragmentSyncerConfig,
} from './SyncInterface';

interface HasIDType {
  id: string;
}

function hasID(data: {__typename?: string; id?: string | number}): data is HasIDType {
  return data.hasOwnProperty('id');
}

function getDeclarationName(fragmentName: TDeclarativeFragmentName): TDeclarationName {
  return fragmentName.replace('GraphQL', '') as TDeclarationName;
}

type PossibleStoreShapes<TDeclaration extends TDeclarationName> =
  | {[id: string]: RESTTypes[TDeclaration]}
  | RESTTypes[TDeclaration]
  | null
  | void;

type MappingStoreShape<TDeclaration extends TDeclarationName> = {
  [id: string]: RESTTypes[TDeclaration];
} | null;

// This class exists primarily to constrain the types of a generic Syncer to ones that can be handled by our
// declarative translations system. It is not intended to be instantiated directly, but rather as one of the
// subclasses below.
class DeclarativeSyncer<TDeclaration extends TDeclarationName> extends Syncer<
  PossibleStoreShapes<TDeclaration>,
  GraphQLTypes[TDeclaration]
> {
  declarationName: TDeclaration;

  constructor(
    declarationName: TDeclaration,
    {stores, shouldSubscribeToRedux}: {stores: Array<Store>; shouldSubscribeToRedux: boolean}
  ) {
    super();
    this.declarationName = declarationName;
    Object.defineProperty(this, 'stores', {value: stores, writable: false});
    Object.defineProperty(this, 'shouldSubscribeToRedux', {value: shouldSubscribeToRedux, writable: false});
  }

  readFromStore(): {current: PossibleStoreShapes<TDeclaration>; equalityRef: unknown} | void {
    throw new Error('Not implemented');
  }

  writeToApolloCache(inputs: PossibleStoreShapes<TDeclaration>): void {
    throw new Error('Not implemented');
  }

  writeToStore(graphQLResult: Array<GraphQLTypes[TDeclaration]>): void {
    throw new Error('Not implemented');
  }
}

function defaultGetIDFromGraphQL(gqlObj: HasIDType): string {
  return gqlObj.id;
}

function getIDFromGraphQL<TDeclaration extends TDeclarationName>(
  gqlObj: GraphQLTypes[TDeclaration],
  config: MaybeCanGetIDFromGraphQL<TDeclaration>
): string {
  if (config.getIDFromGraphQL) {
    return config.getIDFromGraphQL(gqlObj);
  } else if (hasID(gqlObj)) {
    return defaultGetIDFromGraphQL(gqlObj);
  } else if (isInaccessible(gqlObj)) {
    return gqlObj.invalidId;
  } else {
    throw new Error('Encountered unidentifiable object');
  }
}

function getRESTObjsByIDFromGraphQL<TDeclaration extends TDeclarationName>(
  declarationName: TDeclaration,
  graphQLModels: Array<GraphQLTypes[TDeclaration]>,
  config: MaybeCanGetIDFromGraphQL<TDeclaration>
): {[id: string]: RESTTypes[TDeclaration]} {
  const restModelEntries: Array<[string, RESTTypes[TDeclaration]]> = graphQLModels.map((gqlObj) => [
    getIDFromGraphQL(gqlObj, config),
    graphQLToREST(declarationName, gqlObj),
  ]);

  return objectFromEntries(restModelEntries);
}

// Base ModelSyncer class is responsible for implementing shared behavior for writeToStore between the
// Symmetric and Asymmetric versions below, and is not intended to be instantiated derectly.
class ModelSyncer<TDeclaration extends TDeclarationName> extends DeclarativeSyncer<TDeclaration> {
  config: BaseModelSyncerConfig<TDeclaration>;

  constructor(
    declarationName: TDeclaration,
    storeOptions: {stores: Array<Store>; shouldSubscribeToRedux: boolean},
    config: BaseModelSyncerConfig<TDeclaration>
  ) {
    super(declarationName, storeOptions);
    this.config = config;
  }

  // We prefer to index by the GraphQL type id here rather than by the REST type because in general GraphQL is
  // better behaved when it comes to identifiers
  writeToStore(graphQLModels: Array<GraphQLTypes[TDeclaration]>): void {
    this.config.dispatchUpdates(getRESTObjsByIDFromGraphQL(this.declarationName, graphQLModels, this.config));
  }
}

// Declarative syncer that only sync's from GraphQL > REST. This pattern is used for model syncers that
// overlap with a root collection syncer, and as a result reading from store to write to Apollo cache would be
// duplicated syncing work.
export class AsymmetricModelSyncer<TDeclaration extends TDeclarationName> extends ModelSyncer<TDeclaration> {
  constructor(declarationName: TDeclaration, config: BaseModelSyncerConfig<TDeclaration>) {
    super(declarationName, {stores: [], shouldSubscribeToRedux: false}, config);
  }

  readFromStore(): void {}

  writeToApolloCache(): void {}
}

export class SymmetricModelSyncer<TDeclaration extends TDeclarationName> extends ModelSyncer<TDeclaration> {
  config: SymmetricModelSyncerConfig<TDeclaration>;

  constructor(declarationName: TDeclaration, config: SymmetricModelSyncerConfig<TDeclaration>) {
    super(
      declarationName,
      {stores: config.stores ?? [], shouldSubscribeToRedux: !config.stores?.length},
      config
    );
    this.config = config;
  }

  readFromStore(): {current: MappingStoreShape<TDeclaration>; equalityRef: unknown} {
    const storeData = this.config.readFromStore();
    return storeData.current != null
      ? {current: filterMapToNonNulls(storeData.current), equalityRef: storeData.equalityRef}
      : {current: storeData.current, equalityRef: storeData.equalityRef};
  }

  writeToApolloCache(inputs: MappingStoreShape<TDeclaration>): void {
    if (inputs) {
      declarativeWriteRESTObjsToApollo(this.declarationName, Object.values(inputs));
    }
  }
}

class AccessibleOrInaccessibleSyncer<
  TDeclaration extends TDeclarationName,
> extends DeclarativeSyncer<TDeclaration> {
  config: AccessibleOrInaccessibleSyncerConfig<TDeclaration>;

  constructor(declarationName: TDeclaration, config: AccessibleOrInaccessibleSyncerConfig<TDeclaration>) {
    super(declarationName, {stores: config.stores ?? [], shouldSubscribeToRedux: !config.stores?.length});
    this.config = config;
  }

  readFromStore(): {current: MappingStoreShape<TDeclaration>; equalityRef: unknown} {
    const storeData = this.config.readFromStore();
    return storeData.current != null
      ? {current: filterMapToNonNulls(storeData.current), equalityRef: storeData.equalityRef}
      : {current: storeData.current, equalityRef: storeData.equalityRef};
  }

  writeToStore(graphQLObjs: Array<GraphQLTypes[TDeclaration]>): void {
    // TypeScript has trouble making a couple of logical leaps here necessary to achive the types we expect to
    // see, so we are choosing to cast and ignore here explicitly. In particular, it struggles with the fact
    // that technically GraphQLTypes[TDeclaration] can produce the InaccessibleInfo type because of how it was
    // architected, which would make the exclusion statement below nonsensical.

    // @ts-expect-error
    const accessibleRESTObjsByID: {
      [id: string]: Exclude<RESTTypes[TDeclaration], InaccessibleInfoREST>;
      // @ts-expect-error
    } = getRESTObjsByIDFromGraphQL(this.declarationName, accessibleObjs(graphQLObjs), this.config);

    const inaccessibleIds = _.unique(graphQLObjs.flatMap((b) => (isInaccessible(b) ? [b.invalidId] : [])));

    this.config.dispatchAccessibleOrInaccessibleUpdates(accessibleRESTObjsByID, inaccessibleIds);
  }

  writeToApolloCache(inputs: MappingStoreShape<TDeclaration>): void {
    if (inputs) {
      declarativeWriteRESTObjsToApollo(this.declarationName, Object.values(inputs));
    }
  }
}

export class RootCollectionSyncer<
  TDeclaration extends TDeclarationName,
> extends DeclarativeSyncer<TDeclaration> {
  config: RootCollectionSyncerConfig<TDeclaration>;

  constructor(declarationName: TDeclaration, config: RootCollectionSyncerConfig<TDeclaration>) {
    super(declarationName, {stores: config.stores ?? [], shouldSubscribeToRedux: !config.stores});
    this.config = config;
  }

  readFromStore(): {current: RESTTypes[TDeclaration] | null; equalityRef: unknown} {
    return this.config.readCollectionFromStore();
  }

  writeToApolloCache(inputs: RESTTypes[TDeclaration] | null): void {
    if (inputs) {
      declarativeWriteRESTObjsToApollo(this.declarationName, [inputs]);
    }
  }

  writeToStore(graphQLResult: Array<GraphQLTypes[TDeclaration]>): void {
    this.config.dispatchCollectionUpdates(graphQLArrayToREST(this.declarationName, graphQLResult)[0]);
  }
}

function isRootCollectionConfig<TDeclaration extends TDeclarationName>(
  config: DeclarativeSyncerConfig<TDeclaration>
): config is RootCollectionSyncerConfig<TDeclaration> {
  return config != null && config.hasOwnProperty('readCollectionFromStore');
}

function isSymmetricModelConfig<TDeclaration extends TDeclarationName>(
  config: DeclarativeSyncerConfig<TDeclaration>
): config is SymmetricModelSyncerConfig<TDeclaration> {
  return config != null && config.hasOwnProperty('readFromStore');
}

function isAccessibleOrInaccessibleConfig<TDeclaration extends TDeclarationName>(
  config: DeclarativeSyncerConfig<TDeclaration>
): config is AccessibleOrInaccessibleSyncerConfig<TDeclaration> {
  return config != null && config.hasOwnProperty('dispatchAccessibleOrInaccessibleUpdates');
}

// Casts to DeclarativeSycerConfig from TFragmentSyncerConfig if not null
function asDeclarativeConfig(
  config: TFragmentSyncerConfig<TDeclarativeFragmentName>
): DeclarativeSyncerConfig<TDeclarationName> | null {
  if (config) {
    return config as DeclarativeSyncerConfig<TDeclarationName>;
  } else {
    return null;
  }
}

export default class SyncerFactory {
  // The DeclarativeSyncerConfig interface is too complex for TypeScript to handle with all of the types
  // produced by our declarative translations system cross the functions with their inputs and outputs
  // defined in SyncInterface. For now, we will bypass the typechecking here and elsewhere in this file in
  // order to avoid the 'Expression produces a union type that is too complex to represent' error here.
  private static getSyncDefinition<T extends TDeclarativeFragmentName>(
    fragmentName: T,
    config: TFragmentSyncerConfig<T>
  ): TFragmentSyncer<T> {
    // @ts-expect-error: Fixme; introduced by typescript 4.6 upgrade
    const declarativeConfig = asDeclarativeConfig(config);
    if (!declarativeConfig) {
      throw new Error(`Missing syncer config for ${fragmentName}`);
    }

    const declarationName = getDeclarationName(fragmentName);

    if (isRootCollectionConfig(declarativeConfig)) {
      // @ts-expect-error
      return new RootCollectionSyncer(declarationName, declarativeConfig);
    } else if (isAccessibleOrInaccessibleConfig(declarativeConfig)) {
      return new AccessibleOrInaccessibleSyncer(declarationName, declarativeConfig);
    } else if (isSymmetricModelConfig(declarativeConfig)) {
      return new SymmetricModelSyncer(declarationName, declarativeConfig);
    } else {
      return new AsymmetricModelSyncer(declarationName, declarativeConfig);
    }
  }

  public static getFactorySyncDefinitions(fragmentNameToSyncConfig: SyncDeclarationInterface): SyncInterface {
    return _.mapObject(fragmentNameToSyncConfig, (syncConfig, fragmentName: TDeclarativeFragmentName) =>
      this.getSyncDefinition(fragmentName, syncConfig)
    );
  }
}
